<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="error.message" />
        <v-form ref="form" @submit.prevent="onSubmit" color="green">
          <v-card-title class="headline">{{ $t('@winbackForm/create') }}</v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height: 400px; overflow: auto">
            <v-flex xs12>
              <v-select
                v-model="form.winbackType"
                :items="winbackTypes"
                :disabled="submitting"
                :label="$t('@winbackForm/reason') + ' *'"
                :rules="[validateRequired]"
                single-line
                ref="type"
                class="my-0 mb-2 px-1"
                persistent-hint
                :error-messages="validationError(error.errorField, errorFieldMap.winbackType)"
              ></v-select>
              <v-textarea
                v-model="form.userComment"
                color="green darken-1"
                class="px-1 mb-3"
                auto-grow
                rows="2"
                ref="comment"
                :label="$t('@winbackForm/internalComment')"
                :error-messages="validationError(error.errorField, errorFieldMap.userComment)"
              />
              <v-select
                v-model="form.campaignId"
                :items="winbackCampaigns"
                :disabled="submitting"
                :label="$t('@winbackForm/campaign')"
                :rules="[]"
                single-line
                ref="type"
                class="my-0 mb-2 px-1"
                persistent-hint
                :error-messages="validationError(error.errorField, errorFieldMap.campaignId)"
              ></v-select>
            </v-flex>
            <h3 class="subheading mb-2">{{ $t('@winbackForm/history') }}</h3>
            <v-data-table
              :headers="headers"
              :items="items"
              :loading="loading"
              :total-items="pagination.totalItems"
              :pagination.sync="pagination"
              :rows-per-page-items="rowsPerPageItems"
              item-key="_id"
              :hide-actions="hideActions"
            >
              <template v-slot:items="props">
                <td>{{ props.item.createdAt | defaultDateTimeFormatter }}</td>
                <td>{{ createdBy(props.item.byEmployee) }}</td>
              </template>
            </v-data-table>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn color="red darken-1" flat @click.stop="closeModal">
              {{ $t('@winbackForm/cancelButtonLabel') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn primary flat :disabled="isDisabled" type="submit">
              {{ $t('@winbackForm/sendButtonLabel') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import ErrorsList from '../../components/ErrorsList';
import { WinbackTypes } from '../../constants';
import { getErrorMessage, getErrorObject, validationError } from '../../errorHandlers';
import { defaultDateTimeFormatter, emojiRegex, serializeQuery, textRegex } from '../../utils';

const errorFieldMap = {
  userComment: 'userComment',
  winbackType: 'winbackType',
  campaignId: 'campaignId',
};

export default {
  components: { ErrorsList },
  props: {
    contract: {
      type: Object,
    },
  },

  data() {
    return {
      submitting: false,
      form: {
        userComment: '',
        winbackType: '',
        campaignId: null,
      },
      error: {
        message: '',
        field: '',
      },
      headers: [
        {
          text: this.$t('@winbackForm/created'),
          align: 'left',
          sortable: false,
          value: 'date',
        },
        { text: this.$t('@winbackForm/createdBy'), value: 'byEmployee', sortable: false },
      ],
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 5,
        totalItems: 0,
      },
      items: [],
      loading: false,
      rowsPerPageItems: [5, 10, 15],
      lastQueryString: '',
      validationError,
      errorFieldMap,
      winbackCampaigns: [],
    };
  },

  mounted() {
    this.fetch();
    this.fetchWinbackCampaigns();
    this.$nextTick(() => this.$refs.type.focus());
  },

  methods: {
    async fetch(force) {
      const qs = serializeQuery(this.query);

      if (!force && (this.lastQueryString === qs || this.loading)) {
        return;
      }

      try {
        this.loading = true;
        const {
          data: { data, meta },
        } = await this.$http.get(`/api/insurances/${this.contract.id}/winback?${qs}`);
        this.items = data || [];
        this.lastQueryString = qs;
        this.pagination.totalItems = meta.total;
        this.error.message = '';
      } catch (e) {
        this.error.message = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    async fetchWinbackCampaigns() {
      const qs = serializeQuery({
        type: 'winback',
        active: true,
        skip: 0,
        take: 100,
        sortBy: 'availableFrom',
        sortOrder: 'DESC',
      });

      const {
        data: { items },
      } = await this.$http.get(`/api/campaigns?${qs}`);

      const campaigns = items
        .filter((campaign) => campaign.hasStarted === true && campaign.hasEnded === false)
        .filter((campaign) => campaign.productIds.includes(this.contract.product.id))
        .map((campaign) => ({
          value: campaign.id,
          text: campaign.name,
        }));

      this.winbackCampaigns = [
        { value: null, text: this.$t('@winbackForm/noCampaign') },
        ...campaigns,
      ];
    },

    closeModal() {
      this.reset();
      this.$emit('dismiss');
    },

    async onSubmit() {
      this.error = { message: '', field: '' };
      if (
        this.form.userComment &&
        (emojiRegex.test(this.form.userComment) || !textRegex.test(this.form.userComment))
      ) {
        this.error = {
          message: this.$t('@winbackForm/invalidCommentError'),
          field: errorFieldMap.userComment,
        };
      }
      if (!this.form.winbackType) {
        this.error = {
          message: this.$t('@winbackForm/invalidTypeError'),
          field: errorFieldMap.winbackType,
        };
      }
      if (!this.error.message) {
        await this.sendWinback(this.form);
      }
    },

    async sendWinback(form) {
      const data = {
        comment: form.userComment.replace(emojiRegex, ''),
        type: form.winbackType,
        campaignId: form.campaignId,
      };

      try {
        this.error.field = '';
        this.submitting = true;
        await this.$http.post(`/api/insurances/${this.contract.id}/winback`, data);
        this.reset();
      } catch (e) {
        const { errorField: field, errorMessage: message } = getErrorObject(e);

        this.error = { field, message };
      } finally {
        this.submitting = false;
      }
    },

    reset() {
      this.$emit('dismiss');
      this.error = { message: '', field: '' };
      this.userComment = '';
      this.loading = false;
    },
    validateRequired(val) {
      const isFilled = val !== undefined && val !== null && val !== '';
      return isFilled || this.$t('@newContractForm/emptyRequiredFieldErrorMessage');
    },
    createdBy(user) {
      return user
        ? `${user.firstName} ${user.lastName}`
        : this.$t('@auditTrailEventsList/systemUserName');
    },
  },

  computed: {
    hideActions() {
      return !this.pagination.totalItems || this.pagination.totalItems < 5;
    },

    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const query = {
        skip,
        take,
      };

      return query;
    },

    winbackTypes() {
      return Object.keys(WinbackTypes).map((el) => ({
        value: el,
        text: this.$t(WinbackTypes[el]),
      }));
    },

    isDisabled() {
      return !this.form.winbackType || this.submitting;
    },
  },

  filters: {
    defaultDateTimeFormatter,
  },

  watch: {
    pagination() {
      this.fetch();
    },
  },
};
</script>

<style scoped>
>>> .v-datatable__actions__pagination {
  display: none;
}
table.v-table tbody td.column-title {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.54) !important;
}
</style>
